import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useEffect } from 'react';
import { articleContents, latestArticles } from '@/mocks/hoobuyData';

const PT_MONTHS: Record<string, string> = {
  janeiro: '01', fevereiro: '02', março: '03', abril: '04', maio: '05', junho: '06',
  julho: '07', agosto: '08', setembro: '09', outubro: '10', novembro: '11', dezembro: '12',
};

function parseDatePt(dateStr: string): string {
  const parts = dateStr.split(' de ');
  if (parts.length !== 3) return '2026-06-26';
  const day = parts[0].padStart(2, '0');
  const month = PT_MONTHS[parts[1].toLowerCase()] || '06';
  const year = parts[2];
  return `${year}-${month}-${day}`;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = latestArticles.find((a) => a.slug === slug);
  const content = article?.content ? articleContents[article.content] : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hoobuy.motorcycles';

  if (!article || !content) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-14">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">Artigo não encontrado</h1>
            <p className="text-foreground-500 mb-6">O artigo que você procura não está disponível.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              Voltar ao Início
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isoDate = parseDatePt(content.date);
  const articleUrl = `${siteUrl}/artigo/${article.slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: content.title,
    description: article.excerpt,
    datePublished: isoDate,
    dateModified: isoDate,
    author: {
      '@type': 'Organization',
      name: 'Equipe Hoobuy Motorcycles',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Hoobuy Motorcycles',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/879fae149d7d175a673508cd1be74143.jpeg',
      },
    },
    url: articleUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    inLanguage: 'pt-BR',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Início',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Guia de Compras',
        item: `${siteUrl}/#artigos`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: content.tag,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-24 pb-14">
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
          />

          <nav aria-label="Breadcrumb" className="mb-8">
            <ol className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 flex-wrap">
              <li>
                <Link to="/" className="hover:text-primary-600 transition-colors">
                  Início
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-[10px]"></i>
              </li>
              <li>
                <Link to="/#artigos" className="hover:text-primary-600 transition-colors">
                  Guia de Compras
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-[10px]"></i>
              </li>
              <li>
                <span className="text-foreground-600">{content.tag}</span>
              </li>
            </ol>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-accent-500 text-[10px] font-bold text-background-50 whitespace-nowrap">
                {content.tag}
              </span>
              <span className="text-xs text-foreground-400 whitespace-nowrap">
                Atualizado em {content.date}
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground-950 leading-tight mb-4">
              {content.title}
            </h1>

            <p className="text-sm md:text-base text-foreground-500">
              Escrito pela equipe Hoobuy Motorcycles — Especialistas em planilhas e links para compradores brasileiros
            </p>
          </header>

          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 space-y-8">
            {content.sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="text-lg md:text-xl font-bold text-foreground-950 mt-8 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, pIdx) => (
                  <p key={pIdx} className="text-sm md:text-base leading-relaxed mb-4">
                    {p.split('**').map((part, i) =>
                      i % 2 === 1 ? (
                        <strong key={i} className="text-foreground-950">
                          {part}
                        </strong>
                      ) : (
                        part
                      ),
                    )}
                  </p>
                ))}
              </section>
            ))}

            <section className="mt-8 p-5 md:p-6 rounded-xl bg-primary-50 border border-primary-200">
              <h2 className="text-lg font-bold text-primary-800 mb-3">
                Pronto para começar a usar o Melhor Hoobuy Spreadsheet 2026?
              </h2>
              <p className="text-sm text-primary-700 mb-4">
                Acesse agora nossa planilha completa com mais de 1000 links atualizados e fornecedores verificados. Economize tempo e compre com segurança.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Acessar Hoobuy Sheet
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-sm"></i>
                  </span>
                </a>
                <Link
                  to="/#artigos"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-primary-300 text-primary-700 text-sm font-medium hover:bg-primary-100 transition-all whitespace-nowrap"
                >
                  Mais Artigos
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </span>
                </Link>
              </div>
            </section>
          </div>

          <footer className="mt-10 pt-6 border-t border-background-200">
            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-400">
              <span>Atualizado em {content.date}</span>
              <span>·</span>
              <span>Escrito pela equipe Hoobuy Motorcycles</span>
            </div>
            <p className="mt-3 text-xs text-foreground-400 leading-relaxed">
              Este conteúdo é apenas informativo. Não vendemos produtos diretamente. As marcas mencionadas pertencem aos seus respectivos proprietários. Os links fornecidos direcionam para plataformas de terceiros.
            </p>
          </footer>
        </article>
      </main>

      <Footer />
    </div>
  );
}